import axios, { InternalAxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'

// Extend Axios request config to include metadata and retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    requestId: string
    startTime: number
  }
  _retry?: boolean
}

// Enhanced logging utility
const generateRequestId = () => Math.random().toString(36).substr(2, 9)

const logRequest = (requestId: string, config: any) => {
  const logData = {
    timestamp: new Date().toISOString(),
    requestId,
    method: config.method?.toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    fullUrl: `${config.baseURL}${config.url}`,
    headers: {
      ...config.headers,
      Authorization: config.headers.Authorization ? 'Bearer ***' : undefined
    },
    data: config.data ? (typeof config.data === 'string' ?
      (config.data.includes('password') ? '[DATA WITH PASSWORD - REDACTED]' : config.data) :
      { ...config.data, password: config.data.password ? '***' : undefined }) : undefined,
    params: config.params
  }

  console.log(`🚀 [${requestId}] Frontend API Request: ${config.method?.toUpperCase()} ${config.url}`, logData)
}

const logResponse = (requestId: string, response: any, startTime: number) => {
  const duration = Date.now() - startTime
  const logData = {
    timestamp: new Date().toISOString(),
    requestId,
    status: response.status,
    statusText: response.statusText,
    duration: `${duration}ms`,
    url: response.config.url,
    responseSize: JSON.stringify(response.data).length + ' bytes',
    headers: {
      'content-type': response.headers['content-type'],
      'content-length': response.headers['content-length']
    },
    data: response.data
  }

  console.log(`✅ [${requestId}] Frontend API Response: ${response.status} ${response.statusText}`, logData)
}

const logError = (requestId: string, error: any, startTime: number) => {
  const duration = Date.now() - startTime
  const logData = {
    timestamp: new Date().toISOString(),
    requestId,
    duration: `${duration}ms`,
    message: error.message,
    status: error.response?.status,
    statusText: error.response?.statusText,
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    responseData: error.response?.data,
    stack: error.stack
  }

  console.error(`❌ [${requestId}] Frontend API Error: ${error.message}`, logData)
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
})

console.log('🔧 Frontend API initialized', {
  timestamp: new Date().toISOString(),
  baseURL: API_BASE_URL,
  environment: import.meta.env.MODE,
  userAgent: navigator.userAgent
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: string) => void
  reject: (error: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token!)
    }
  })
  
  failedQueue = []
}

api.interceptors.request.use(
  (config: ExtendedAxiosRequestConfig) => {
    const requestId = generateRequestId()
    const startTime = Date.now()

    // Store metadata for response logging
    config.metadata = { requestId, startTime }

    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Log the request
    logRequest(requestId, config)

    return config
  },
  (error) => {
    console.error('🚨 Frontend API Request Error:', error)
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    // Log successful response
    const config = response.config as ExtendedAxiosRequestConfig
    if (config.metadata) {
      logResponse(config.metadata.requestId, response, config.metadata.startTime)
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig

    // Log error
    if (originalRequest?.metadata) {
      logError(originalRequest.metadata.requestId, error, originalRequest.metadata.startTime)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔄 [AUTH] Token expired, attempting refresh...', {
        timestamp: new Date().toISOString(),
        url: originalRequest.url,
        isRefreshing
      })

      if (isRefreshing) {
        console.log('🔄 [AUTH] Already refreshing, queuing request...', {
          timestamp: new Date().toISOString(),
          url: originalRequest.url
        })
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          console.log('🔄 [AUTH] Retrying request with new token...', {
            timestamp: new Date().toISOString(),
            url: originalRequest.url
          })
          return api(originalRequest)
        }).catch((err) => {
          console.error('🚨 [AUTH] Failed to retry request:', err)
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        console.log('🔄 [AUTH] Requesting token refresh...', {
          timestamp: new Date().toISOString()
        })
        const response = await api.post('/auth/refresh')
        const { accessToken } = response.data

        localStorage.setItem('accessToken', accessToken)
        processQueue(null, accessToken)

        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        console.log('✅ [AUTH] Token refreshed successfully, retrying original request...', {
          timestamp: new Date().toISOString(),
          url: originalRequest.url
        })
        return api(originalRequest)
      } catch (refreshError) {
        console.error('❌ [AUTH] Token refresh failed, redirecting to login...', {
          timestamp: new Date().toISOString(),
          error: refreshError
        })
        processQueue(refreshError, null)
        localStorage.removeItem('accessToken')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    if (error.response?.data?.error) {
      console.log('🔔 [TOAST] Showing error toast:', error.response.data.error)
      toast.error(error.response.data.error)
    }

    return Promise.reject(error)
  }
)

export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  
  register: (userData: any) =>
    api.post('/auth/register', userData),
  
  logout: () =>
    api.post('/auth/logout'),
  
  getCurrentUser: () =>
    api.get('/auth/me'),
  
  refreshToken: () =>
    api.post('/auth/refresh'),
  
  changePassword: (passwords: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/change-password', passwords),
  
  forgotPassword: (email: string, churchId?: number) =>
    api.post('/auth/forgot-password', { email, churchId }),

  resetPassword: (email: string, code: string, newPassword: string) =>
    api.post('/auth/reset-password', { email, code, newPassword })
}

export const churchApi = {
  getChurches: () => api.get('/churches'),
  getChurch: (id: number) => api.get(`/churches/${id}`),
  createChurch: (churchData: any) => api.post('/churches', churchData),
  registerChurch: (churchData: any) => api.post('/churches/register', churchData), // Public registration
  updateChurch: (id: number, updates: any) => api.put(`/churches/${id}`, updates),
  deleteChurch: (id: number) => api.delete(`/churches/${id}`)
}

export const competitionApi = {
  getCompetitions: () => api.get('/competitions'),
  getCompetition: (id: number) => api.get(`/competitions/${id}`),
  createCompetition: (competitionData: any) => api.post('/competitions', competitionData),
  updateCompetition: (id: number, updates: any) => api.put(`/competitions/${id}`, updates),
  deleteCompetition: (id: number) => api.delete(`/competitions/${id}`)
}

export const usersApi = {
  getUsers: (params?: any) => api.get('/users', { params }),
  getUser: (id: number) => api.get(`/users/${id}`),
  createUser: (userData: any) => api.post('/users', userData),
  registerParticipant: (userData: any) => api.post('/users/register', userData), // Public registration
  updateUser: (id: number, updates: any) => api.put(`/users/${id}`, updates),
  deleteUser: (id: number) => api.delete(`/users/${id}`),
  promoteUser: (id: number, role: string) => api.patch(`/users/${id}/role`, { role }),
  resetUserPassword: (id: number, newPassword: string) => api.post(`/users/${id}/reset-password`, { newPassword })
}

export const tournamentsApi = {
  getTournaments: (params?: any) => api.get('/tournaments', { params }).then(res => res.data),
  getTournament: (id: number) => api.get(`/tournaments/${id}`).then(res => res.data),
  getById: (id: number) => api.get(`/tournaments/${id}`).then(res => res.data),
  createTournament: (tournamentData: any) => api.post('/tournaments', tournamentData).then(res => res.data),
  updateTournament: (id: number, updates: any) => api.put(`/tournaments/${id}`, updates).then(res => res.data),
  deleteTournament: (id: number) => api.delete(`/tournaments/${id}`).then(res => res.data),

  // Bracket and match management
  getMatches: (tournamentId: number) => api.get(`/tournaments/${tournamentId}/matches`).then(res => res.data || []),
  getParticipants: (tournamentId: number) => api.get(`/tournaments/${tournamentId}/participants`).then(res => res.data || []),
  updateMatch: (matchId: string, updates: any) => api.put(`/matches/${matchId}`, updates).then(res => res.data),
  moveParticipant: (participantId: number, newPosition: number) =>
    api.patch(`/participants/${participantId}/position`, { position: newPosition }).then(res => res.data),

  // Bracket generation
  generateBracket: (tournamentId: number, options: any) =>
    api.post(`/tournaments/${tournamentId}/generate-bracket`, options).then(res => res.data),

  // Schedule management
  getSchedule: (tournamentId: number) => api.get(`/tournaments/${tournamentId}/schedule`).then(res => res.data),
  updateSchedule: (tournamentId: number, schedule: any) =>
    api.put(`/tournaments/${tournamentId}/schedule`, schedule).then(res => res.data),

  // Real-time updates
  subscribeToUpdates: (tournamentId: number) =>
    api.get(`/tournaments/${tournamentId}/subscribe`).then(res => res.data),

  // Registration
  getChurches: () => api.get('/churches').then(res => res.data || []),
  register: (tournamentId: number, data: any) =>
    api.post(`/tournaments/${tournamentId}/register`, data).then(res => res.data),
}

export default api